/**
 * eyeFollowScene — the THREE-facing half of Eye Follow (ENG-1060).
 *
 * `eyeFollowRuntime` deliberately takes plain numbers so it stays importable
 * under `node --test`. This module is the one place that turns a scene + camera
 * into those numbers, so the two renderers that own an avatar (AvatarModel for a
 * GLB rig, OGSModel for a splat) share ONE definition of "direction from the
 * eyes to the camera" and one definition of body yaw. Duplicating six lines of
 * vector math at both call sites is how the two would end up disagreeing about
 * which way the avatar is facing.
 */

import * as THREE from 'three'
import { eyeFollowRuntime } from './eyeFollowRuntime.js'

// Scratch — reused every frame, nothing allocated on the hot path.
const _camPos = new THREE.Vector3()
const _pivotPos = new THREE.Vector3()
const _toCam = new THREE.Vector3()
const _bodyQuat = new THREE.Quaternion()
const _bodyEuler = new THREE.Euler()
const _fwd = new THREE.Vector3()
const _ref = { yaw: 0, pitch: 0 }

// Pivot preference: the eyes sit in the head, so aim from there. `head` is the
// MetaHuman bone name headFollowRuntime already resolves; the neck is a usable
// stand-in, and the root is the last resort (a splat branch with no skeleton).
// Whatever this resolves to is used as the aim ORIGIN and the reference FRAME —
// no `isBone` test, because a GLTF importer is free to hand back a plain
// Object3D and a type check there would silently disable the head-relative aim.
const PIVOT_NAMES = ['head', 'neck_02', 'neck_01']

let _cachedRoot = null
let _cachedPivot = null
let _retryIn = 0
let _lastPivot = null
// Whether the current frame's aim ran from a caller-supplied localPivot (the
// splat path) — pivotName would otherwise report null and read as "unresolved".
let _usedLocalPivot = false
// Aim origin in PIVOT-LOCAL space — the midpoint of the eyeball meshes,
// measured once when the pivot resolves. Null until then (aim from the bone).
let _aimOffset = null

// How many frames to wait before re-searching for the pivot bone. The search is
// a name walk over the whole rig, so it must not run every frame — but it MUST
// keep retrying: the first call happens before the rig GLB has been added, when
// the walk necessarily finds nothing.
const PIVOT_RETRY_FRAMES = 15

function resolvePivot(root) {
  // ONLY a successful find is cached. Caching the fallback was a real bug caught
  // in the browser: the first frame runs before the GLB's bones exist, so the
  // root got cached forever and the aim ran from the avatar's FEET — measured on
  // myra, y 0.086 instead of the head bone's 1.46, which pinned pitch at its
  // limit from every camera position and made the eyes permanently look up.
  if (_cachedRoot === root && _cachedPivot && _cachedPivot !== root) return _cachedPivot
  if (_cachedRoot !== root) { _cachedRoot = root; _cachedPivot = null; _retryIn = 0 }
  if (_retryIn > 0) { _retryIn--; return _cachedPivot ?? root }

  for (const name of PIVOT_NAMES) {
    const obj = root.getObjectByName?.(name)
    if (obj) {
      _cachedPivot = obj
      _aimOffset = computeAimOffset(root, obj)
      return obj
    }
  }
  // No named bone yet, or none at all (a splat bundle with no skeleton — those
  // pass an explicit localPivot instead). Aim from the root for now and look
  // again shortly; the root's world position is at the feet, so this is a
  // stopgap, never a resting state.
  _retryIn = PIVOT_RETRY_FRAMES
  return root
}

// The aim ORIGIN should be the eyes, not the head bone: measured on myra the
// eye midpoint sits 6.2 cm ABOVE and 7.4 cm IN FRONT of the head bone's
// origin, which at a conversational half-metre is a constant ~8 deg of aim
// error — the gaze lands visibly above/past a close camera, and humans read
// that instantly as "not looking at me". Resolved once per pivot resolve
// (never per frame): the eyeball meshes are the morph meshes whose bind
// geometry is tiny (an eyeball is ~1.2 cm; the head/teeth are not), their
// skinned vertices are sampled for a world midpoint, and the result is stored
// PIVOT-LOCAL so it rides the head pose for free. Returns null when no eyeball
// meshes exist (splats; rigs with baked-in eyes) — the bone origin then stands.
function computeAimOffset(root, pivot) {
  try {
    const eyes = []
    root.traverse(o => {
      if (!o.isMesh || !o.morphTargetInfluences || !o.geometry) return
      if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere()
      if (o.geometry.boundingSphere && o.geometry.boundingSphere.radius < 0.05) eyes.push(o)
    })
    if (!eyes.length) return null
    const mid = new THREE.Vector3()
    const tmp = new THREE.Vector3()
    for (const m of eyes) {
      const count = m.geometry.attributes.position.count
      const step = Math.max(1, count >> 5) // ~32 samples per eyeball
      const acc = new THREE.Vector3()
      let n = 0
      for (let i = 0; i < count; i += step) { m.getVertexPosition(i, tmp); acc.add(tmp); n++ }
      acc.divideScalar(n)
      m.updateWorldMatrix(true, false)
      acc.applyMatrix4(m.matrixWorld)
      mid.add(acc)
    }
    mid.divideScalar(eyes.length)
    pivot.updateWorldMatrix(true, false)
    return pivot.worldToLocal(mid)
  } catch {
    return null // any surprise (detached geometry, no skeleton yet) → bone origin
  }
}

/**
 * Advance the eye aim one frame from live scene state. Call at most once per
 * frame, from whichever renderer owns the avatar.
 *
 * @param {THREE.Object3D} root  avatar root (groupRef.current / the OGS avatar root)
 * @param {THREE.Camera} camera  the live camera
 * @param {number} gate  `__eyefollow__` sentinel weight (0 = node off)
 * @param {number} delta seconds since last frame
 * @param {{x:number,y:number,z:number}|null} localPivot  aim origin in ROOT-LOCAL
 *   space, for a rig with no named head bone. A splat bundle's eye branch knows
 *   exactly where its eyes are (`controller.midpoint()`); without it the aim
 *   would start at the avatar root — near the feet — and every pitch would read
 *   as looking up at a camera that is level with the face.
 * @returns {boolean} whether the aim advanced (false = nothing to aim from)
 */
export function updateEyeFollowFromScene(root, camera, gate, delta, localPivot = null) {
  if (!root || !camera) return false

  // Even at gate 0 the runtime must keep ticking, or the smoothed weight would
  // freeze at its last value and the eyes would stay parked when the node is
  // switched off. That is the same reason headFollow smooths weight always.
  _usedLocalPivot = !!localPivot
  _lastPivot = localPivot ? null : resolvePivot(root)
  if (localPivot) {
    // Accepts either a THREE-ish {x,y,z} or the `[x,y,z]` tuple src/ogs/math
    // uses (`controller.midpoint()` returns the latter).
    if (Array.isArray(localPivot)) _pivotPos.set(localPivot[0], localPivot[1], localPivot[2])
    else _pivotPos.set(localPivot.x, localPivot.y, localPivot.z)
    root.localToWorld(_pivotPos)
  } else if (_aimOffset && _lastPivot !== root) {
    // Aim from the measured eye midpoint, expressed pivot-local so it follows
    // the head pose. Only when the pivot is a real bone — the offset was
    // measured against it, and applying it to the root fallback would be junk.
    _pivotPos.copy(_aimOffset)
    _lastPivot.localToWorld(_pivotPos)
  } else {
    _lastPivot.getWorldPosition(_pivotPos)
  }
  camera.getWorldPosition(_camPos)
  _toCam.subVectors(_camPos, _pivotPos)
  if (_toCam.lengthSq() < 1e-8) return false // camera sits on the pivot

  // The reference frame the eyes rest in. When a head BONE was resolved, that
  // bone's own world orientation is the reference: head-follow may already have
  // turned it toward the target, and the eyes must only take the remainder (see
  // eyeAim.aimAngles for the measurement that forced this). Falling back to the
  // root is right for a rig with no head bone, where nothing turns the head.
  //
  // MetaHuman bone convention (headFollowRuntime documents it): local -Y is
  // forward, local +X is up the spine. This MUST be read after applyHeadFollow
  // has posed the bone for this frame — `mixer.update()` resets it to the clip
  // pose, so reading earlier sees a forward-facing head and double-counts the
  // whole angle. AvatarModel's call order is load-bearing for that reason.
  const pivot = _lastPivot
  if (!localPivot && (!pivot || pivot === root)) {
    // The head bone has not resolved yet (the first frames run before the rig
    // GLB is in the tree). Aiming from the BODY here is not a harmless
    // approximation: with head-follow also running it hands the eyes the whole
    // angle and they slam to their clamp for those frames, which reads as a
    // flick to the side on load. Make the reference the direction itself, so the
    // residual is zero and the eyes simply rest until the bone appears.
    _ref.yaw = Math.atan2(_toCam.x, _toCam.z)
    _ref.pitch = Math.asin(Math.max(-1, Math.min(1, _toCam.y / _toCam.length())))
  } else if (pivot && pivot !== root) {
    // updateWorldMatrix FIRST, and this is load-bearing: applyHeadFollow writes
    // bone LOCAL quaternions, and matrixWorld is only refreshed when something
    // else walks the tree. Reading it blind meant the reference could be a frame
    // stale — or the untouched clip pose — which silently degrades this back to a
    // body-relative aim and hands the eyes the whole angle again. Depending on
    // another system having updated the matrix is not a dependency worth having.
    pivot.updateWorldMatrix(true, false)
    pivot.getWorldQuaternion(_bodyQuat)
    _fwd.set(0, -1, 0).applyQuaternion(_bodyQuat)
    // The bone's -Y IS the face's forward: verified against the bind pose,
    // where it reads exactly yaw 0 / pitch 0 relative to the body. Do NOT
    // 'calibrate' an offset from the live pose — the idle clip tilts the head
    // a few degrees, the eyes are SUPPOSED to compensate for that, and a
    // correction sampled mid-idle bakes the tilt out of the reference and
    // into the gaze as a permanent miss (measured: it made pitch WORSE).
    _ref.yaw = Math.atan2(_fwd.x, _fwd.z)
    _ref.pitch = Math.asin(Math.max(-1, Math.min(1, _fwd.y)))
  } else {
    root.getWorldQuaternion(_bodyQuat)
    _bodyEuler.setFromQuaternion(_bodyQuat, 'YXZ')
    _ref.yaw = _bodyEuler.y
    _ref.pitch = 0
  }
  // Published for diagnosis: "what frame did the eyes measure from" is the first
  // question when the aim looks wrong, and it used to need a bespoke console
  // snippet to answer.
  eyeFollowRuntime.state.refYaw = _ref.yaw
  eyeFollowRuntime.state.refPitch = _ref.pitch


  eyeFollowRuntime.update(_toCam.x, _toCam.y, _toCam.z, _ref, gate, delta)
  _maybeLog(gate)
  return true
}

// ?eyelog=1 — a human-paced console readout of the live aim, so "are the eyes
// moving?" is answerable by watching the console instead of pasting probes.
// Throttled to 2Hz and only while the aim is actually changing; gated behind
// the URL param so it costs nothing in normal runs.
let _eyeLog = null // null = not yet resolved; false = off
let _logAt = 0
let _logYaw = 999
function _maybeLog(gate) {
  if (_eyeLog === null) {
    try { _eyeLog = new URLSearchParams(window.location.search).get('eyelog') === '1' } catch { _eyeLog = false }
  }
  if (!_eyeLog) return
  const now = performance.now()
  const s = eyeFollowRuntime.state
  if (now - _logAt < 500 || Math.abs(s.yaw - _logYaw) < 0.5 * Math.PI / 180) return
  _logAt = now
  _logYaw = s.yaw
  const overlay = eyeFollowRuntime.arkitOverlay()
  let peak = 0
  for (let i = 0; i < overlay.length; i++) if (overlay[i] > peak) peak = overlay[i]
  console.log(
    `[eyeFollow] eyes ${(s.yaw * 57.3).toFixed(1)}°/${(s.pitch * 57.3).toFixed(1)}°`
    + ` head ${(s.refYaw * 57.3).toFixed(1)}° gate ${gate.toFixed(2)} weight ${s.weight.toFixed(2)}`
    + ` pivot ${eyeFollowPivotName()} morph ${peak.toFixed(2)}`
  )
}

/** Forget the cached pivot — call on character switch, where the rig changes. */
export function resetEyeFollowScene() {
  _cachedRoot = null
  _cachedPivot = null
  _retryIn = 0
  _aimOffset = null
  _usedLocalPivot = false
  eyeFollowRuntime.reset()
}

/**
 * Which object the aim is ACTUALLY running from this frame — `_lastPivot`, not
 * the cache. Reading the cache was actively misleading during debugging: it
 * reported 'head' while the frame was still aiming from the root, which sent me
 * looking for the fault in the wrong place.
 */
export function eyeFollowPivotName() {
  if (_usedLocalPivot) return '(local pivot)'
  return _lastPivot?.name || (_lastPivot ? '(root — unresolved)' : null)
}

// Published on the runtime's existing global rather than a second one: "am I
// aiming from the head or still from the root?" is the first question to ask
// when the eyes look wrong, and it belongs next to the angles it explains.
if (typeof window !== 'undefined' && window.__EYE_FOLLOW__) {
  Object.defineProperty(window.__EYE_FOLLOW__, 'pivotName', { get: eyeFollowPivotName, configurable: true })
}
